import { useEffect } from 'react';

/**
 * Mutate document head from inside a React component — no extra deps.
 * Restores any tag we created (not just edited) when the component unmounts
 * so SPA navigation never leaks meta from previous routes.
 *
 * Usage:
 *   useSEO({
 *     title: 'Pricing — Sovereign IQ',
 *     description: '…',
 *     canonical: 'https://sovereigniq.org/pricing',
 *     keywords: 'country intelligence pricing, DFI…',
 *     og: { title: '…', description: '…', image: '…', url: '…' },
 *   });
 */
function upsertMeta(selector, attrName, attrValue, content) {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute('content');
  el.setAttribute('content', content);
  return { el, prev, created };
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute('href');
  el.setAttribute('href', href);
  return { el, prev, created };
}

export default function useSEO({ title, description, keywords, canonical, og = {}, twitter = {} } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    const restorers = [];

    if (title) document.title = title;

    if (description) {
      const r = upsertMeta('meta[name="description"]', 'name', 'description', description);
      restorers.push(() => {
        if (r.created) r.el.remove();
        else if (r.prev != null) r.el.setAttribute('content', r.prev);
      });
    }
    if (keywords) {
      const r = upsertMeta('meta[name="keywords"]', 'name', 'keywords', keywords);
      restorers.push(() => {
        if (r.created) r.el.remove();
        else if (r.prev != null) r.el.setAttribute('content', r.prev);
      });
    }
    if (canonical) {
      const r = upsertLink('canonical', canonical);
      restorers.push(() => {
        if (r.created) r.el.remove();
        else if (r.prev != null) r.el.setAttribute('href', r.prev);
      });
    }

    // OpenGraph — page-specific overrides
    const ogMap = {
      'og:title':       og.title       || title,
      'og:description': og.description || description,
      'og:image':       og.image       || 'https://sovereigniq.org/logo-big.png',
      'og:url':         og.url         || canonical,
      'og:type':        og.type        || 'website',
    };
    Object.entries(ogMap).forEach(([prop, content]) => {
      if (!content) return;
      const r = upsertMeta(`meta[property="${prop}"]`, 'property', prop, content);
      restorers.push(() => {
        if (r.created) r.el.remove();
        else if (r.prev != null) r.el.setAttribute('content', r.prev);
      });
    });

    // Twitter
    const twMap = {
      'twitter:title':       twitter.title       || og.title       || title,
      'twitter:description': twitter.description || og.description || description,
      'twitter:image':       twitter.image       || og.image       || 'https://sovereigniq.org/logo-big.png',
      'twitter:card':        'summary_large_image',
    };
    Object.entries(twMap).forEach(([nm, content]) => {
      if (!content) return;
      const r = upsertMeta(`meta[name="${nm}"]`, 'name', nm, content);
      restorers.push(() => {
        if (r.created) r.el.remove();
        else if (r.prev != null) r.el.setAttribute('content', r.prev);
      });
    });

    return () => {
      document.title = prevTitle;
      restorers.forEach((fn) => { try { fn(); } catch (_) { /* noop */ } });
    };
  }, [title, description, keywords, canonical, JSON.stringify(og), JSON.stringify(twitter)]);
}
